import React from 'react'
import { useTranslation } from 'react-i18next'
import { FallbackProps } from 'react-error-boundary'
import { Button } from '@pandiafi/design-system'
import { useNavigate } from 'react-router-dom'

import { ResidentAppError } from '../../../common/error'
import { ResidentAppErrorCode } from '../../../common/types'

import { Error } from 'components/Error'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { ResidentAppErrorBoundary } from 'components/ResidentAppErrorBoundary/ResidentAppErrorBoundary'

interface ErrorHandlerProps {
  children: React.ReactNode
  reservableCategory?: string
  resetView?: () => void
}

export const ErrorHandler: React.FC<ErrorHandlerProps> = ({
  children,
  reservableCategory,
  resetView,
}) => {
  const [t] = useTranslation(['reservations'])
  const navigate = useNavigate()
  const {
    saunaReservationPath,
    commonAreaReservationsPath,
    laundryReservationsPath,
    gymReservationsPath,
  } = useContractPaths()

  const fallbackRender = ({ error, resetErrorBoundary }: FallbackProps) => {
    let url = '/'
    switch (reservableCategory) {
      case 'house_sauna':
        url = saunaReservationPath
        break
      case 'reservable_common_area':
        url = commonAreaReservationsPath
        break
      case 'laundry_room':
      case 'washing_machine':
      case 'allergy_washing_machine':
      case 'dryer':
      case 'mangle':
        url = laundryReservationsPath
        break
      case 'gym':
        url = gymReservationsPath
        break
      default:
        url = '/'
    }

    const RestartButton = (
      <Button
        onClick={() => {
          resetErrorBoundary()
          navigate(url, {
            replace: true,
          })
          if (resetView) {
            resetView()
          }
        }}
      >
        {t('reservations:error.reloadButton')}
      </Button>
    )

    if (error instanceof ResidentAppError) {
      switch (error.errorCode) {
        case ResidentAppErrorCode.CONFIRM_OPERATION_GENERIC_ERROR:
          return (
            <Error
              title={t('reservations:error.oops')}
              action={RestartButton}
            />
          )

        default:
      }
    }

    return (
      <Error
        message={t('reservations:error.commonError')}
        action={RestartButton}
      />
    )
  }

  return (
    <ResidentAppErrorBoundary fallbackRender={fallbackRender}>
      {children}
    </ResidentAppErrorBoundary>
  )
}
